import React, {
    useCallback,
    useEffect,
    useRef,
    useState,
    type ReactNode,
    type RefObject,
} from 'react';

import { useUIColors } from '../../theme';
import { cx, type Styleable } from '../../variants';

/**
 * EAA-1821: an anchored popover whose OPEN STATE, dismissal and positioning are
 * the component's job, and whose trigger is the caller's.
 *
 * Why not antd's `Popover`: it renders into its own portal layer, so a filter
 * menu opened from a table header ends up on a different z-stack from the rest
 * of a screen's own popovers, and it cannot be positioned relative to a `<th>`
 * that scrolls inside a table. Every console screen that needed this
 * (`/console/apps`, `/console/audit`) hand-rolled the same
 * `position: relative` + outside-click + Escape wiring, and each copy dismissed
 * slightly differently. This is that wiring, once.
 *
 * The trigger is a render prop rather than a `trigger` element because the
 * caller's button must receive the ref (Escape restores focus to it) and the
 * `aria-expanded` state — passing an element and cloning it would silently drop
 * a caller's own ref.
 */

export interface FilterPopoverTriggerState {
    open: boolean;
    /** Attach to the trigger — Escape hands focus back to it. */
    ref: RefObject<HTMLButtonElement | null>;
    toggle: () => void;
    close: () => void;
}

export interface FilterPopoverProps extends Styleable {
    trigger: (state: FilterPopoverTriggerState) => ReactNode;
    /** Panel body — rendered only while open. */
    children: ReactNode;
    /** Which edge the panel aligns to. `end` keeps a right-hand column's menu
     *  inside a horizontally scrolling table. */
    align?: 'start' | 'end';
    /** Panel min-width in px. */
    width?: number;
    /** Merged onto the floating panel, not the anchor. */
    panelClassName?: string;
}

export function FilterPopover({
    trigger,
    children,
    align = 'start',
    width = 220,
    panelClassName,
    className,
    style,
}: FilterPopoverProps): React.ReactElement {
    const colors = useUIColors();
    const [open, setOpen] = useState(false);
    const rootRef = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);

    const close = useCallback(() => setOpen(false), []);
    const toggle = useCallback(() => setOpen((prev) => !prev), []);

    useEffect(() => {
        if (!open) return;
        function handlePointerDown(event: MouseEvent) {
            if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
                setOpen(false);
            }
        }
        document.addEventListener('mousedown', handlePointerDown);
        return () => document.removeEventListener('mousedown', handlePointerDown);
    }, [open]);

    // EAA-1821: Escape is handled on the ROOT, not on `document`, so it can hand
    // focus back to the trigger — a document-level listener has no idea which
    // trigger opened which menu, and dropping focus on <body> after a dismissal
    // is what makes a keyboard user lose their place in a table header.
    const handleKeyDown = useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key !== 'Escape') return;
        event.stopPropagation();
        setOpen(false);
        triggerRef.current?.focus();
    }, []);

    return (
        <div
            ref={rootRef}
            className={cx('ui-filter-popover', className)}
            onKeyDown={handleKeyDown}
            style={{ position: 'relative', display: 'inline-flex', flex: 'none', ...style }}
        >
            {trigger({ open, ref: triggerRef, toggle, close })}
            {open && (
                <div
                    className={cx('ui-filter-popover__panel', panelClassName)}
                    style={{
                        position: 'absolute',
                        top: '100%',
                        left: align === 'start' ? 0 : 'auto',
                        right: align === 'end' ? 0 : 'auto',
                        zIndex: 20,
                        marginTop: 6,
                        minWidth: width,
                        padding: 6,
                        borderRadius: 8,
                        background: colors.surfaceElevated,
                        boxShadow: `0 0 0 1px ${colors.border}, 0 16px 40px rgba(16, 24, 40, 0.14)`,
                    }}
                >
                    {children}
                </div>
            )}
        </div>
    );
}

export default FilterPopover;
